import React, { useState, useEffect } from "react";
import { Calendar, FileText, Link2, Upload, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ContentWorkflow = ({ onGetStarted }) => {
  const [calendarKeywords, setCalendarKeywords] = useState([]);
  const [publishingStep, setPublishingStep] = useState(0);

  // Keywords for calendar animation
  const keywords = [
    "AI Writing Tools",
    "SEO Best Practices",
    "Content Strategy",
    "Link Building",
    "Keyword Research",
    "Digital Marketing",
    "Blog Optimization",
    "Content Planning",
    "SEO Analytics",
    "Growth Hacking",
    "Email Marketing",
    "Social Media Tips",
    "Conversion Rate",
    "User Experience",
    "Web Performance"
  ];

  // Animate calendar keywords
  useEffect(() => {
    const interval = setInterval(() => {
      setCalendarKeywords(prev => {
        if (prev.length >= 15) {
          return [];
        }
        return [...prev, keywords[prev.length]];
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  // Publishing animation cycle
  useEffect(() => {
    const interval = setInterval(() => {
      setPublishingStep(prev => (prev + 1) % 4);
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="mb-4">
            <span className="text-xs font-bold tracking-widest text-green-500 uppercase">
              Simple 4-Step Process
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            From <span className="text-green-500">keywords</span> to published content
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Automated content creation that works while you sleep. Just set it up once and watch your blog grow daily.
          </p>
        </div>

        {/* Four Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Step 1: Keywords for 30 Days */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
            <div className="bg-green-50 p-6 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[240px]">
                {/* Calendar Grid */}
                <div className="bg-white rounded-xl shadow-lg border-2 border-green-500 p-4">
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                    <Calendar className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-bold text-gray-700">30 Days</span>
                  </div>
                  
                  {/* Calendar Grid - 5x3 = 15 days visible */}
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: 15 }).map((_, index) => (
                      <motion.div
                        key={index}
                        className="aspect-square bg-gray-50 rounded-lg border border-gray-200 flex flex-col items-center justify-center p-1 overflow-hidden"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ 
                          scale: calendarKeywords[index] ? 1 : 0.8,
                          opacity: calendarKeywords[index] ? 1 : 0.3,
                          backgroundColor: calendarKeywords[index] ? "#dcfce7" : "#f9fafb"
                        }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <span className="text-[8px] font-bold text-gray-400 mb-0.5">
                          {index + 1}
                        </span>
                        <AnimatePresence>
                          {calendarKeywords[index] && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="w-2 h-2 bg-green-500 rounded-full"
                            />
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>

                  {/* Counter */}
                  <motion.div
                    className="mt-3 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <span className="text-xs font-bold text-green-600">
                      {calendarKeywords.length} keywords planned
                    </span>
                  </motion.div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-green-600">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Plan 30 days ahead
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Get a month's worth of strategic keywords automatically. Each day has a targeted topic ready to rank.
              </p>
            </div>
          </div>

          {/* Step 2: AI Article Generation */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
            <div className="bg-green-50 p-6 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[240px]">
                <div className="bg-white rounded-xl shadow-lg border-2 border-green-500 p-4">
                  {/* AI Writing Header */}
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                    <FileText className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-bold text-gray-700">AI Writer</span>
                    <div className="ml-auto flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="w-1 h-1 bg-green-500 rounded-full"
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Article Preview */}
                  <div className="space-y-2">
                    {/* Title */}
                    <motion.div
                      className="space-y-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="h-2 bg-green-200 rounded w-full" />
                      <div className="h-2 bg-green-200 rounded w-3/4" />
                    </motion.div>

                    {/* Paragraphs */}
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="space-y-1 pt-2"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + i * 0.3 }}
                      >
                        <div className="h-1 bg-gray-300 rounded w-full" />
                        <div className="h-1 bg-gray-300 rounded w-full" />
                        <div className="h-1 bg-gray-300 rounded w-4/5" />
                      </motion.div>
                    ))}
                  </div>

                  {/* Keywords Badge */}
                  <motion.div
                    className="mt-3 flex gap-1 flex-wrap"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                  >
                    {["SEO", "2000+ words", "Optimized"].map((tag, i) => (
                      <span 
                        key={i}
                        className="text-[8px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold"
                      >
                        {tag}
                      </span>
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-green-600">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  AI writes the article
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Advanced AI creates long-form, SEO-optimized content using top keyword research tools. Every article is publication-ready.
              </p>
            </div>
          </div>

          {/* Step 3: Interlinking & YouTube */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
            <div className="bg-green-50 p-6 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[240px]">
                <div className="bg-white rounded-xl shadow-lg border-2 border-green-500 p-4">
                  {/* Enhancement Header */}
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                    <Link2 className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-bold text-gray-700">Enhancing</span>
                  </div>

                  {/* Internal Links */}
                  <div className="space-y-2 mb-3">
                    {[0, 1].map((i) => (
                      <motion.div
                        key={i}
                        className="flex items-center gap-2 p-2 bg-green-50 rounded-lg"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + i * 0.2 }}
                      >
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" />
                        <div className="flex-1 space-y-1">
                          <div className="h-1 bg-green-300 rounded w-full" />
                          <div className="h-1 bg-green-300 rounded w-2/3" />
                        </div>
                        <Link2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                      </motion.div>
                    ))}
                  </div>

                  {/* YouTube Video */}
                  <motion.div
                    className="bg-gray-900 rounded-lg overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                  >
                    <div className="aspect-video bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center relative">
                      <motion.div
                        className="w-8 h-8 bg-white rounded-full flex items-center justify-center"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <div className="w-0 h-0 border-l-[8px] border-l-red-600 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1" />
                      </motion.div>
                      <div className="absolute top-1 left-1 text-[6px] font-bold text-white px-1 bg-black/50 rounded">
                        YouTube
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-green-600">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Add links & videos
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Smart internal linking connects your content. Relevant YouTube videos are embedded to boost engagement and time on page.
              </p>
            </div>
          </div>

          {/* Step 4: Auto Publish */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
            <div className="bg-green-50 p-6 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[240px]">
                <div className="bg-white rounded-xl shadow-lg border-2 border-green-500 p-4">
                  {/* Publishing Header */}
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                    <Upload className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-bold text-gray-700">Publishing</span>
                  </div>

                  {/* CMS Grid */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {["wordpress", "webflow", "notion", "shopify", "wix", "webhook"].map((platform, i) => (
                      <motion.div
                        key={platform}
                        className="aspect-square bg-gray-50 rounded-lg border-2 flex items-center justify-center p-2"
                        initial={{ borderColor: "#e5e7eb" }}
                        animate={{ 
                          borderColor: publishingStep === Math.floor(i / 1.5) ? "#22c55e" : "#e5e7eb",
                          backgroundColor: publishingStep === Math.floor(i / 1.5) ? "#dcfce7" : "#f9fafb"
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <img 
                          src={`/images/integration-logos/${platform}-icon.svg`} 
                          alt={platform}
                          className="w-full h-full object-contain"
                        />
                      </motion.div>
                    ))}
                  </div>

                  {/* Success Badge */}
                  <AnimatePresence mode="wait">
                    {publishingStep === 3 && (
                      <motion.div
                        key="success"
                        className="flex items-center justify-center gap-2 p-2 bg-green-100 rounded-lg"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <motion.div
                          className="w-2 h-2 bg-green-500 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 0.5 }}
                        />
                        <span className="text-[10px] font-bold text-green-700">
                          Published to all platforms
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-green-600">4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Publish everywhere
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Content goes live automatically across WordPress, Webflow, Notion, Shopify, Wix, and more. Zero manual work needed.
              </p>
            </div>
          </div>
        </div>

        
      </div>
    </section>
  );
};

export default ContentWorkflow;

